import * as React from 'react';
import { ProfileProvider, useProfile } from '@app/Contexts/ProfileProvider';
import { Link, NavLink, Outlet, useParams } from 'react-router-dom';
import {
  Button,
  ButtonGroup,
  Container,
  Dropdown,
  Nav,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullseye,
  faCalendar,
  faCopy,
  faEdit,
  faEnvelope,
  faExclamationCircle,
  faFileCircleCheck,
  faFilePdf,
  faFileSignature,
  faLaptop,
  faLock,
  faMailBulk,
  faPencil,
  faPills,
  faPrint,
  faQuestionCircle,
  faQuoteLeft,
  faQuoteRight,
  faRocket,
  faSms,
  faSpinner,
  faSync,
  faTasks,
  faTrash,
  faUserCircle,
  faUserMd,
  faVoicemail,
} from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { t } from 'i18next';
import { faBlackTie } from '@fortawesome/free-brands-svg-icons';
import type { DropdownMenuEntry } from '@app/Components/DropdownMenu';
import { DropdownMenu, DropdownMenuItem } from '@app/Components/DropdownMenu';
import { AssetURLType, getAssetURL } from '@app/Utilities/AssetURL';
import { IndFlagButton } from '@app/Components/Ind/IndFlagButton';
import clsx from 'clsx';
import type { Ind } from '@api/Entities/Ind/Ind';
import { IndFlag } from '@api/Entities/Ind/IndFlag';
import { useRepository } from '@app/Hooks/useRepository';
import { UserFlagRepository, UserRepository } from '@api/index';
import type { UserFlag } from '@api/Entities/User/UserFlag';
import { useAuth } from '@app/Contexts/AuthProvider';
import { UserFlagButton } from '@app/Components/User/UserFlagButton';
import { getMatchingUserFlags } from '@app/Utilities/Ind/IndFlag';

type ProfileSnapshotFlagsProps = {
  flags: Array<UserFlag>;
};

const ProfileSnapshotFlags = ({ flags }: ProfileSnapshotFlagsProps) => {
  return (
    <div className="mt-1">
      {flags.map((flag) => {
        return (
          <UserFlagButton selected={true} className="me-1" key={flag.getFlagId()} flag={flag} />
        );
      })}
    </div>
  );
};

type ProfileSnapshotProps = {
  profile: Ind;
};

const ProfileSnapshot = ({ profile }: ProfileSnapshotProps) => {
  const [userFlags, setUserFlags] = React.useState<Array<UserFlag>>([]);
  const [userFlagsLoading, setUserFlagsLoading] = React.useState<boolean>(true);

  const userFlagRepository = useRepository(UserFlagRepository);
  const session = useAuth();

  React.useEffect(() => {
    userFlagRepository.getUserFlags(session.getUser().getUserId()).then((userFlags) => {
      const allUserFlags = userFlags.getItems();
      const filteredFlags = getMatchingUserFlags(profile, allUserFlags);

      setUserFlags(filteredFlags);
      setUserFlagsLoading(false);
    });
  }, []);

  const menu: DropdownMenuEntry[] = [
    {
      type: 'item',
      key: 'quickEdit',
      icon: <FontAwesomeIcon className="text-primary" icon={faEdit} />,
      label: t('agent.profile.common.actionMenu.quickEdit'),
      onClick: () => console.log(''),
    },
    {
      type: 'item',
      key: 'makeAlert',
      icon: <FontAwesomeIcon className="text-primary" icon={faExclamationCircle} />,
      label: t('agent.profile.common.actionMenu.makeAlert'),
      onClick: () => console.log(''),
    },
    {
      type: 'item',
      key: 'makeActivity',
      icon: <FontAwesomeIcon className="text-primary" icon={faTasks} />,
      label: t('agent.profile.common.actionMenu.makeActivity'),
      to: `/agents/${profile.getIndId()}/activities/new`,
    },
    {
      type: 'item',
      key: 'setAppointment',
      icon: <FontAwesomeIcon className="text-primary" icon={faCalendar} />,
      label: t('agent.profile.common.actionMenu.setAppointment'),
      to: `/agents/${profile.getIndId()}/appointments/new`,
    },

    { type: 'divider', key: 'd1' },

    {
      type: 'item',
      key: 'sendMail',
      icon: <FontAwesomeIcon className="text-secondary" icon={faMailBulk} />,
      label: t('agent.profile.common.actionMenu.sendMail'),
      onClick: () => console.log('send mail'),
    },
    {
      type: 'item',
      key: 'sendEmail',
      icon: <FontAwesomeIcon className="text-secondary" icon={faEnvelope} />,
      label: t('agent.profile.common.actionMenu.sendEmail'),
      onClick: () => console.log('send email'),
    },
    {
      type: 'item',
      key: 'sendSMS',
      icon: <FontAwesomeIcon className="text-secondary" icon={faSms} />,
      label: t('agent.profile.common.actionMenu.sendSMS'),
      onClick: () => console.log('send sms'),
    },
    {
      type: 'item',
      key: 'sendVoicemail',
      icon: <FontAwesomeIcon className="text-secondary" icon={faVoicemail} />,
      label: t('agent.profile.common.actionMenu.sendVoicemail'),
      onClick: () => console.log('send voicemail'),
    },
    {
      type: 'item',
      key: 'sendConference',
      icon: <FontAwesomeIcon className="text-secondary" icon={faLaptop} />,
      label: t('agent.profile.common.actionMenu.sendConference'),
      onClick: () => console.log('send conference'),
    },
    {
      type: 'item',
      key: 'syncContact',
      icon: <FontAwesomeIcon className="text-secondary" icon={faSync} />,
      label: t('agent.profile.common.actionMenu.syncContact'),
      onClick: () => console.log('sync contact'),
    },

    { type: 'divider', key: 'd2' },

    {
      type: 'item',
      key: 'clientPortal',
      icon: <FontAwesomeIcon className="text-success" icon={faUserCircle} />,
      label: t('agent.profile.common.actionMenu.clientPortal'),
      to: `/agents/${profile.getIndId()}/portal`,
    },
    {
      type: 'item',
      key: 'healthAssessment',
      icon: <FontAwesomeIcon className="text-success" icon={faQuestionCircle} />,
      label: t('agent.profile.common.actionMenu.healthAssessment'),
      to: `/agents/${profile.getIndId()}/health-assessment`,
    },

    { type: 'divider', key: 'd3' },

    {
      type: 'item',
      key: 'advisorReview',
      icon: <FontAwesomeIcon className="text-secondary" icon={faBlackTie} />,
      label: t('agent.profile.common.actionMenu.advisorReview'),
      onClick: () => console.log('advisor review'),
    },
    {
      type: 'item',
      key: 'profilePrint',
      icon: <FontAwesomeIcon className="text-secondary" icon={faPrint} />,
      label: t('agent.profile.common.actionMenu.profilePrint'),
      onClick: () => console.log('print'),
    },
    {
      type: 'item',
      key: 'copyView',
      icon: <FontAwesomeIcon className="text-secondary" icon={faCopy} />,
      label: t('agent.profile.common.actionMenu.copyView'),
      onClick: () => console.log('copy view'),
    },

    { type: 'divider', key: 'd4' },

    {
      type: 'item',
      key: 'deleteProfile',
      icon: <FontAwesomeIcon className="text-danger" icon={faTrash} />,
      label: t('agent.profile.common.actionMenu.deleteProfile'),
      onClick: () => console.log(''),
    },
  ];

  const profileImage = profile.getIndProfilePic() ? profile.getIndProfilePic() : null;
  const profileFlags = profile.getFlags();

  return (
    <div className="d-flex align-items-top mb-3">
      <div className="px-2 text-center">
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="tooltip-top">{t('agent.profile.common.editImage')}</Tooltip>}
        >
          <button role="button" className="profile-avatar border-0 me-0 mb-0 ms-0 p-0">
            {profileImage ? (
              <div className="avatar-image rounded-circle overflow-hidden border border-black">
                <img src={profileImage} className="w-100 h-100" />
              </div>
            ) : (
              <FontAwesomeIcon
                widthAuto
                size="3x"
                style={{ display: 'block' }}
                icon={faUserCircle}
              />
            )}
          </button>
        </OverlayTrigger>
      </div>
      <div className="flex-grow-1 ps-1">
        <p className="m-0">{profile.getIndStatus()}</p>
        <h4 className="mb-0 fw-bold">{profile.getIndName()}</h4>
        {userFlags.length > 0 && <ProfileSnapshotFlags flags={userFlags} />}
      </div>
      <div className="px-2 text-center">
        <Button variant="success ms-1" size="sm">
          <FontAwesomeIcon icon={faRocket} /> {t('agent.profile.common.enrollButton')}
        </Button>
        <Dropdown as={ButtonGroup} className="ms-1">
          <Dropdown.Toggle size="sm" variant="info">
            {t('agent.profile.common.actionButton')}
          </Dropdown.Toggle>

          <DropdownMenu className="bg-white" items={menu} />
        </Dropdown>
      </div>
    </div>
  );
};

type IconButtonProps = {
  title: string;
  icon: IconProp;
  active?: boolean;
};

const IconButton = ({ title, icon, active = false }: IconButtonProps) => {
  const classList = ['lead', active ? 'text-primary' : 'text-white-50'];

  return (
    <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">{title}</Tooltip>}>
      <FontAwesomeIcon size="1x" className={clsx(classList)} icon={icon} />
    </OverlayTrigger>
  );
};

export const AgentProfileLoading = () => {
  return (
    <main className="flex-grow-1 d-flex">
      <Container
        fluid
        className="d-flex align-items-center justify-content-center"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <FontAwesomeIcon icon={faSpinner} spin className="text-primary" size="10x" />
        <span className="visually-hidden">{t('common.loading')}</span>
      </Container>
    </main>
  );
};

export const AgentProfileLayout = () => {
  const { profile, loading } = useProfile();

  // Get the current page
  const { '*': subroute } = useParams();

  const activeSection = subroute?.split('/')[0] || 'profile';

  if (loading) return <AgentProfileLoading />;

  return (
    <main className="flex-grow-1 d-flex">
      {/* Left nav */}
      <Nav className="flex-column border-end bg-dark py-2">
        <Nav.Link as={Button} className="bg-transparent">
          <IconButton title={t('agent.profile.common.nav.enableDisableLead')} icon={faBullseye} />
        </Nav.Link>

        <Nav.Link as={NavLink} to={`/agent/profile/${profile.getIndId()}/quote`}>
          <IconButton
            title={t('agent.profile.common.nav.quote')}
            icon={faQuoteRight}
            active={activeSection === 'quote'}
          />
        </Nav.Link>

        <Nav.Link as={NavLink} to={`/agent/profile/${profile.getIndId()}/soa`}>
          <IconButton
            title={t('agent.profile.common.nav.soa')}
            icon={faFileCircleCheck}
            active={activeSection === 'soa'}
          />
        </Nav.Link>

        <hr className="dropdown-divider my-2 border border-white opacity-50 mx-1" />

        <Nav.Link as={NavLink} to={`/agent/profile/${profile.getIndId()}`}>
          <IconButton
            title={t('agent.profile.common.nav.profile')}
            icon={faUserCircle}
            active={activeSection === 'profile'}
          />
        </Nav.Link>
        <Nav.Link as={NavLink} to={`/agent/profile/${profile.getIndId()}/policies`}>
          <IconButton
            title={t('agent.profile.common.nav.policies')}
            icon={faFileSignature}
            active={activeSection === 'policies'}
          />
        </Nav.Link>
        <Nav.Link as={NavLink} to={`/agent/profile/${profile.getIndId()}/providers`}>
          <IconButton
            title={t('agent.profile.common.nav.providers')}
            icon={faUserMd}
            active={activeSection === 'providers'}
          />
        </Nav.Link>
        <Nav.Link as={NavLink} to={`/agent/profile/${profile.getIndId()}/rx`}>
          <IconButton
            title={t('agent.profile.common.nav.rxAndHealth')}
            icon={faPills}
            active={activeSection === 'rx'}
          />
        </Nav.Link>
        <Nav.Link as={NavLink} to={`/agent/profile/${profile.getIndId()}/notes`}>
          <IconButton
            title={t('agent.profile.common.nav.notesAndLogs')}
            icon={faPencil}
            active={activeSection === 'notes'}
          />
        </Nav.Link>
        <Nav.Link as={NavLink} to={`/agent/profile/${profile.getIndId()}/documents`}>
          <IconButton
            title={t('agent.profile.common.nav.docs')}
            icon={faFilePdf}
            active={activeSection === 'documents'}
          />
        </Nav.Link>
        <Nav.Link as={NavLink} to={`/agent/profile/${profile.getIndId()}/private-info`}>
          <IconButton
            title={t('agent.profile.common.nav.privateInfo')}
            icon={faLock}
            active={activeSection === 'private-info'}
          />
        </Nav.Link>
      </Nav>

      {/* Main content */}
      <Container fluid className="flex-grow-1 overflow-auto pt-4 px-4">
        {/* Profile Snapshot */}
        <ProfileSnapshot profile={profile} />
        {/* Page Content */}
        <Outlet />
      </Container>
    </main>
  );
};
